import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { OfflineService } from '../offline/offline.service';
import { Subscription } from 'rxjs';
import { IdbService } from '../idb/idb.service';
import { PWAModel } from '../../interfaces/offline/pwa';
import { DatePipe, formatDate } from '@angular/common';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class PwaBackupService {
  connectionStatusSubscription: Subscription;
  connectionStatus: boolean;

  constructor(
    private api: ApiService,
    private local: LocalStorageService,
    private idb: IdbService,
    private offline: OfflineService,
    public datePipe: DatePipe
  ) {
    this.connectionStatusSubscription =
      this.offline.currentConnectionStatus.subscribe((connectionStatus) => {
        this.connectionStatus = connectionStatus;
      });
  }

  postPWALogs() {
    let userId = this.local.get('uid');
    let userName = this.local.get('name');
    let loggedDate = formatDate(
      new Date(),
      'yyyy-MM-ddTHH:mm:ss.SSSZ',
      'en-US'
    );
    let functionName = this.local.get('role');

    this.idb.getAllPWABackUpNeedingLogged().subscribe({
      next: (pwaLogs: PWAModel[]) => {
        if (pwaLogs.length > 0) {
          pwaLogs.forEach((pwaLog) => {
            this.api
              .LogPWAJsonBackUp(
                pwaLog,
                userId,
                userName,
                loggedDate,
                functionName
              )
              .subscribe({
                next: (response) => {
                  if (response) {
                    this.idb
                      .markPWABackUpAsLogged(pwaLog.pwaBackUpId!)
                      .subscribe({
                        next: (response) => {
                          console.log('PWA Log successfully posted');
                        },
                      });
                  }
                },
                error: (error) => {
                  console.error('Error posting pwa log:', error);
                },
              });
          });
          const currentTime = new Date().getTime();
          try {
            localStorage.setItem(
              'last_pwa_log_time',
              JSON.stringify(currentTime)
            );
          } catch (error) {
            Sentry.captureEvent({
              message: `Error storing data in localStorage: ${error}`,
              level: 'error',
              tags: {
                date: this.datePipe.transform(new Date(), 'yyyy-MM-dd'),
                user: this.local.get('name'),
              },
            });
          }
        }
      },
      error: (error) => {
        console.error('Error fetching pwa logs:', error);
      },
    });
  }

  deleteOldPWALogs() {
    this.idb.deleteOldPWABackUpData().subscribe({
      next: (response) => {
        console.log('Old PWA Logs deleted');
      },
      error: (error) => {
        console.error('Error deleting old pwa logs:', error);
      },
    });
  }

  // Background task which attempts to sync pwaLogs every 4 hours
  // If the user is offline, it will retry at regular intervals
  startBackgroundTask(checkIfUpdateNeededInterval: number) {
    // Set the interval for the background task
    setInterval(() => {
      if (this.connectionStatus) {
        this.checkAndPostPWALogs();
      } else {
        this.handleOfflineScenario();
      }
    }, checkIfUpdateNeededInterval);
  }

  private checkAndPostPWALogs() {
    // Might need to convert this to a number
    // Doesn't seem to be necessary
    const lastUpdateTime = JSON.parse(
      localStorage.getItem('last_pwa_log_time') || '0'
    );
    const currentTime = new Date().getTime();
    // Need to update in app component too if we change it here
    const updateInterval = 4 * 60 * 60 * 1000; // 4  hours in milliseconds

    // Check if it has been more than the update interval since the last update
    if (currentTime - lastUpdateTime >= updateInterval) {
      this.postPWALogs();
      this.deleteOldPWALogs();
    }
  }

  handleOfflineScenario() {
    const retryInterval = 300000; // 5 minutes in milliseconds
    let retryIntervalId: any; // Variable to store the interval ID

    // Listen for the 'online' event and retry updating the configuration
    window.addEventListener('online', () => {
      this.checkAndPostPWALogs();
    });

    // Retry at regular intervals if the user is offline
    // Once the user is connected, the interval will be cleared
    retryIntervalId = setInterval(() => {
      if (navigator.onLine) {
        this.checkAndPostPWALogs();
        this.deleteOldPWALogs();
        clearInterval(retryIntervalId); // Clear the interval when the user is connected again
      }
    }, retryInterval);
  }

  ngOnDestroy() {
    this.connectionStatusSubscription.unsubscribe();
  }
}
